import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Briefcase, FileText, LayoutDashboard, RadioTower, TestTube2, Users, Workflow } from "lucide-react";

import { AppShell, type AppShellNavSection } from "~/components";
import { ActionErrorFlash } from "~/components/ActionErrorFlash";
import { SignalToasts } from "~/components/SignalToasts";
import { UnfiledDocumentsFlash } from "~/components/UnfiledDocumentsFlash";
import {
  currentUserQueryOptions,
  useLogoutMutation,
  type AuthUser,
} from "~/lib/auth";
import { HalError } from "~/lib/hal";
import { queryClient } from "~/lib/queryClient";
import { useEventChannel } from "~/lib/realtime/useEventChannel";
import { vars } from "~/styles/tokens.css";

// Pathless layout route (`_app`). Everything nested under this route
// is protected — this is where the auth guard lives.
//
// beforeLoad runs before any child route renders. It prefetches the
// current user via the shared query options (cache-hit after the
// first load, so navigating between protected pages is free). On
// 401 it throws a redirect to /login with the current pathname
// captured as the return_to param, so the post-login callback lands
// the user back where they started.
//
// Any error that isn't a 401 is re-thrown and bubbles up to the
// router's error boundary — we don't want to silently swallow a 500.
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ location }) => {
    try {
      await queryClient.ensureQueryData(currentUserQueryOptions);
    } catch (err) {
      if (err instanceof HalError && err.isUnauthorized) {
        throw redirect({
          to: "/login",
          search: { returnTo: location.pathname },
        });
      }
      throw err;
    }
  },
  component: AppLayout,
});

// Nav structure. New sections land with their respective feature
// tickets. Cases were added in TMF-37 alongside the case create/edit
// UI.
// Icons are lucide-react SVGs for crisp, consistent rendering across
// platforms (emoji varied wildly between macOS, Linux, and Windows
// renderers). Size is controlled by AppShell.navItemIcon CSS; stroke
// is inherited from the nav text color so active items pick up the
// green accent automatically.
const ICON_SIZE = 18;
const ICON_STROKE = 2;
const navSections: AppShellNavSection[] = [
  {
    label: "Main",
    items: [
      {
        to: "/",
        icon: <LayoutDashboard size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
        label: "Home",
      },
      {
        to: "/alpha",
        icon: <RadioTower size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
        label: "Alpha",
      },
      {
        to: "/cases",
        icon: <Briefcase size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
        label: "Cases",
      },
      {
        to: "/document-uploads",
        icon: <FileText size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
        label: "Document Uploads",
      },
      {
        to: "/workflows",
        icon: <Workflow size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
        label: "Workflows",
      },
      {
        to: "/users",
        icon: <Users size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
        label: "Users",
      },
    ],
  },
  {
    label: "Dev",
    items: [
      {
        to: "/kitchen-sink",
        icon: <TestTube2 size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
        label: "Kitchen sink",
      },
    ],
  },
];

function AppLayout() {
  const { data: user } = useSuspenseQuery(currentUserQueryOptions);
  // Real-time stale-data invalidation channel (TMF-110/112/114).
  // Mounting here — after the auth-guarded suspense query — means
  // every authenticated route is an SSE subscriber and unauthenticated
  // routes never open a connection.
  useEventChannel();
  const logout = useLogoutMutation();
  const navigate = useNavigate();

  // Alpha is an app pane, not a document: it owns its frame edge to edge,
  // so it opts out of the shell's 1400px reading measure (DESIGN.md §4).
  const isAlpha = useRouterState({
    select: (s) => s.location.pathname.startsWith("/alpha"),
  });

  // The focused-thread workspace is a live call: nothing may pop over the
  // note canvas (DESIGN.md's no-interruption rule). Only that subtree —
  // Tower and the rest of /alpha still get platform toasts.
  const isFocusedThread = useRouterState({
    select: (s) => s.location.pathname.startsWith("/alpha/threads/"),
  });

  const handleSignOut = () => {
    logout.mutate(undefined, {
      onSuccess: () => {
        void navigate({ to: "/login", search: { returnTo: "/" } });
      },
    });
  };

  return (
    <>
      <AppShell
        navSections={navSections}
        user={buildShellUser(user)}
        onSignOut={handleSignOut}
        fullBleed={isAlpha}
      >
        <Outlet />
      </AppShell>
      {!isFocusedThread && (
        <>
          <SignalToasts />
          <UnfiledDocumentsFlash />
          {/* Rejected actions, reported by the mutations themselves through
              lib/actionErrors (TMF-362). */}
          <ActionErrorFlash />
        </>
      )}
    </>
  );
}

// Map the AuthUser HAL resource into the lean shape the shell needs.
// Kept local to this file: the shell doesn't know about auth, and
// auth doesn't know about the shell.
function buildShellUser(user: AuthUser) {
  const firstInitial = initialOf(user.first_name);
  const lastInitial = initialOf(user.last_name);
  const initials = `${firstInitial}${lastInitial}` || "?";
  const name = `${user.first_name} ${user.last_name}`.trim() || user.email;

  return {
    name,
    meta: user.type,
    initials,
    // Gold on navy reads as "this is you" without dedicating a brand
    // surface to it.
    avatarBackground: vars.color.sidebarAccent,
  };
}

function initialOf(value: string): string {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed[0]!.toUpperCase() : "";
}
